import axios from "axios";
import {
  Box,
  TextField as MuiTextField,
  Typography as MuiTypography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  DialogTitle,
  DialogContent,
  IconButton
} from "@mui/material";
import Breadcrumbs from "../components/breadcrumbs/Breadcrumbs";
import { Papercontainer } from "@rewardle/apps.ui.papercontainer.papercontainer";
import { useEffect, useState } from "react";
import { CustomButton } from "../components/tables/table.styled";
import { scrollToTop } from "../utils/commonFunctions";
import Modal from "../components/modal/ModalPage";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components/macro";
import Loader from "../components/loader/Loader";

const apiEnpoint = process.env.REACT_APP_API_BASE_URL;

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center;
  label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

const TypographyModal = styled(MuiTypography)`
  color: rgba(34, 51, 84, 0.7);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4375em;
  padding: 0;
  margin-top: 13px;
`;

const TypographyTitle = styled(MuiTypography)`
  color: var(--light-text-dark, rgba(0, 0, 0, 0.88));
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

interface Address {
  AddressLabel: string;
  Address: {
    PostCode: string;
    AddressType: string;
  };
}

interface UserProfile {
  FirstName: string;
  SurName: string;
  Mobile: string;
  AccountCode: string;
  UserId: string;
  Email: string;
  InitialSignInProvider: string;
  CurrentSignInProvider: string;
  AccountCodeExtraData: string;
  Scans: number;
  Rating: boolean;
  AllowRatingAtNumberOfScan: number;
  Badge: number;
  AssociatedAccountCodes: string[];
  Gender: string;
  Birthday: string;
  Addresses: Address[];
  TwitterUsername?: string;
  InstagramUsername?: string;
  PostcodeHome?: string;
  PostcodeWork?: string;
  SocialHandles?: {
    InstagramName?: string;
    TwitterName?: string;
  };
}

const UserProfileManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [editedProfile, setEditedProfile] = useState<UserProfile | any>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: ""
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (editedProfile?.Email) {
      const emailParts = editedProfile.Email.split("+");
      setShowDeleteButton(!emailParts[1]?.includes("anonymize"));
    }
  }, [editedProfile?.Email]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios({
        method: "get",
        url: `${apiEnpoint}/users/user`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${token}`
        }
      });

      // Transform the data to include PostcodeHome, PostcodeWork and social handles
      const userData = response.data;
      const homeAddress = userData.Addresses?.find(
        (addr: Address) => addr.Address.AddressType === "Home"
      );
      const officeAddress = userData.Addresses?.find(
        (addr: Address) => addr.Address.AddressType === "Office"
      );

      setEditedProfile({
        ...userData,
        PostcodeHome: homeAddress?.Address.PostCode || "",
        PostcodeWork: officeAddress?.Address.PostCode || "",
        Birthday: userData.DateOfBirth?.split(" ")[0] || "",
        // Map social handles to the profile fields
        TwitterUsername: userData.SocialHandles?.TwitterName || "",
        InstagramUsername: userData.SocialHandles?.InstagramName || ""
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const handleInputChange =
    (field: keyof UserProfile) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editedProfile) {
        setEditedProfile({
          ...editedProfile,
          [field]: event.target.value
        });
      }
    };

  const handleSave = async () => {
    setSaveLoading(true);

    try {
      const token = localStorage.getItem("accessToken");

      const userUpdateData = {
        DateOfBirth: editedProfile.Birthday ? editedProfile.Birthday : "",
        FirstName: editedProfile.FirstName ? editedProfile.FirstName : "",
        SurName: editedProfile.SurName ? editedProfile.SurName : "",
        Gender: editedProfile.Gender ? editedProfile.Gender : "",
        SocialHandles: {
          InstagramName: editedProfile.InstagramUsername
            ? editedProfile.InstagramUsername
            : "",
          TwitterName: editedProfile.TwitterUsername
            ? editedProfile.TwitterUsername
            : ""
        }
      };

      await axios({
        method: "put",
        url: `${apiEnpoint}/users/user`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${token}`
        },
        data: userUpdateData
      });

      // Only make the address update API call if either postcode is provided

      const addresses: Address[] = [];

      addresses.push({
        AddressLabel: "Home",
        Address: {
          PostCode: editedProfile.PostcodeHome
            ? editedProfile.PostcodeHome
            : "",
          AddressType: "Home"
        }
      });

      addresses.push({
        AddressLabel: "Office",
        Address: {
          PostCode: editedProfile.PostcodeWork
            ? editedProfile.PostcodeWork
            : "",
          AddressType: "Office"
        }
      });

      await axios({
        method: "put",
        url: `${apiEnpoint}/users/user/addresses`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${token}`
        },
        data: { Addresses: addresses }
      });

      const message = "User profile updated successfully";
      setNotificationMessage({
        message: message,
        type: "success"
      });
      setSaveLoading(false);
      setOpenConfirmDialog(false);
      scrollToTop();

      // Refresh the profile data
      fetchUserProfile();
    } catch (error: any) {
      setOpenConfirmDialog(false);
      const concatenatedMessage = error?.response?.data?.Failures?.map(
        (item: any) => item.Message
      ).join(", ");

      setLoading(false);
      setNotificationMessage({
        message: concatenatedMessage || "Failed to update profile",
        type: "error"
      });
      scrollToTop();
      setSaveLoading(false);
    }
  };

  const handleSaveClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    handleSave();
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      await axios({
        method: "delete",
        url: `${apiEnpoint}/users/user`,
        headers: {
          Authorization: `bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      // Handle successful deletion
      setNotificationMessage({
        message: "Account deleted successfully",
        type: "success"
      });
      // setOpenDeleteDialog(false);
      setDeleteLoading(false);

      setIsDisabled(true);
      // Add a 3-second delay before clearing data and reloading
      // setTimeout(() => {
      //   localStorage.clear();
      //   window.location.reload();
      // }, 3000);
    } catch (error: any) {
      setIsDisabled(false);
      const errorMessage =
        error?.response?.data?.message || "Failed to delete account";
      setNotificationMessage({
        message: errorMessage,
        type: "error"
      });
      setOpenDeleteDialog(false);
      setDeleteLoading(false);
      scrollToTop();
    }
  };

  return (
    <>
      <Breadcrumbs
        heading={"User Profile"}
        subHeading={"User Profile Management"}
        docs={null}
      />
      <Papercontainer elevation={0}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="80vh"
          >
            <Loader />
          </Box>
        ) : (
          <Box>
            <Box>
              {notificationMessage?.message && !isDisabled && (
                <div style={{ marginTop: "2px", marginBottom: "1px" }}>
                  <Alert severity={notificationMessage?.type as any}>
                    {notificationMessage?.message}
                  </Alert>
                </div>
              )}
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <TypographyTitle variant="h4"></TypographyTitle>
              <Box>
                {showDeleteButton && (
                  <CustomButton
                    className={
                      isDisabled ? "blue_button custom_disabled" : "blue_button"
                    }
                    onClick={() => setOpenDeleteDialog(true)}
                    disabled={isDisabled}
                  >
                    Delete Account
                  </CustomButton>
                )}
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  variant="standard"
                  value={editedProfile?.Email || ""}
                  onChange={handleInputChange("Email")}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  variant="standard"
                  value={editedProfile?.FirstName || ""}
                  onChange={handleInputChange("FirstName")}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Surname"
                  variant="standard"
                  value={editedProfile?.SurName || ""}
                  onChange={handleInputChange("SurName")}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  variant="standard"
                  value={editedProfile?.Gender || ""}
                  onChange={handleInputChange("Gender")}
                  SelectProps={{
                    native: true
                  }}
                  disabled={true}
                >
                  <option value=""></option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Birthday"
                  type="date"
                  variant="standard"
                  value={editedProfile?.Birthday || ""}
                  onChange={handleInputChange("Birthday")}
                  InputLabelProps={{
                    shrink: true
                  }}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  variant="standard"
                  value={editedProfile?.Mobile || ""}
                  onChange={handleInputChange("Mobile")}
                  disabled={true}
                />
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12}>
                <TypographyTitle variant="h5">Additional</TypographyTitle>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postcode (Home)"
                  variant="standard"
                  value={editedProfile?.PostcodeHome || ""}
                  onChange={(e) => {
                    if (editedProfile) {
                      setEditedProfile({
                        ...editedProfile,
                        PostcodeHome: e.target.value
                      });
                    }
                  }}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postcode (Work)"
                  variant="standard"
                  value={editedProfile?.PostcodeWork || ""}
                  onChange={(e) => {
                    if (editedProfile) {
                      setEditedProfile({
                        ...editedProfile,
                        PostcodeWork: e.target.value
                      });
                    }
                  }}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Twitter Username"
                  variant="standard"
                  value={editedProfile?.TwitterUsername || ""}
                  onChange={handleInputChange("TwitterUsername")}
                  InputProps={{
                    startAdornment: "@"
                  }}
                  placeholder="Enter your Twitter username"
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Instagram Username"
                  value={editedProfile?.InstagramUsername || ""}
                  onChange={handleInputChange("InstagramUsername")}
                  InputProps={{
                    startAdornment: "@"
                  }}
                  placeholder="Enter your Instagram username"
                  variant="standard"
                  disabled={true}
                />
              </Grid>
            </Grid>
            {/* <Box mt={2}>
              <CustomButton
                className={
                  saveLoading || isDisabled
                    ? "blue_button custom_disabled "
                    : "blue_button"
                }
                onClick={handleSaveClick}
                disabled={saveLoading || isDisabled}
              >
                {"Save Details"}
              </CustomButton>
            </Box> */}
          </Box>
        )}
      </Papercontainer>

      {/* Save user details */}
      <Modal open={openConfirmDialog} setOpen={setOpenConfirmDialog}>
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
        ></DialogTitle>

        <IconButton
          aria-label="close"
          onClick={() => setOpenConfirmDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 2,
            color: (theme: any) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <TypographyModal gutterBottom>
            {"Are you sure you want to update your details?."}
          </TypographyModal>
          <Box
            mt={2}
            sx={{
              display: "flex",
              gap: "10px"
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              style={{ marginTop: 2 }}
              className={
                saveLoading || isDisabled
                  ? "blue_button custom_disabled "
                  : "blue_button"
              }
              onClick={saveLoading || isDisabled ? () => {} : handleConfirmSave}
              disabled={saveLoading || isDisabled}
            >
              {saveLoading ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                "Save "
              )}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              style={{ marginTop: 2 }}
              className={
                saveLoading || isDisabled
                  ? "blue_button custom_disabled "
                  : "blue_button"
              }
              onClick={
                saveLoading || isDisabled
                  ? () => {}
                  : () => setOpenConfirmDialog(false)
              }
              disabled={saveLoading || isDisabled}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Modal>

      {/* Add Delete Account confirmation modal */}
      <Modal open={openDeleteDialog} setOpen={setOpenDeleteDialog}>
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
        ></DialogTitle>

        <IconButton
          aria-label="close"
          onClick={() => setOpenDeleteDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 2,
            color: (theme: any) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        {!isDisabled ? (
          <DialogContent dividers>
            <TypographyModal gutterBottom>
              {" Are you sure you want to delete your account?."}
            </TypographyModal>
            <Box
              mt={2}
              sx={{
                display: "flex",
                gap: "10px"
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                style={{ marginTop: 2 }}
                className="blue_button"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <CircularProgress size={25} color="inherit" />
                ) : (
                  "Delete"
                )}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                style={{ marginTop: 2 }}
                className="blue_button"
                onClick={() => setOpenDeleteDialog(false)}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        ) : (
          <DialogContent dividers>
            <Alert severity={notificationMessage?.type as any}>
              {notificationMessage?.message}
            </Alert>
            <Box
              mt={2}
              sx={{
                display: "flex",
                gap: "10px",
                justifyContent: "center"
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                style={{ marginTop: 2 }}
                className="blue_button"
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
              >
                Logout
              </Button>
            </Box>
          </DialogContent>
        )}
      </Modal>
    </>
  );
};

export default UserProfileManagement;
